import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import HomeLayout from '../layouts/HomeLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import RouteViewComponent from '../layouts/RouterBypass.vue'
import { useUser } from '../stores/useUser'
import { getUser, refreshToken } from '../apis/services'
import { User } from '../apis/types'
import { setExpiryTimestamp } from '../utils/utils'
import { useToast } from 'vuestic-ui'

const { init } = useToast()

const routes: Array<RouteRecordRaw> = [
  {
    name: 'home',
    path: '/',
    component: HomeLayout,
    children: [
      {
        name: 'index',
        path: '/',
        component: () => import('../pages/home/Main.vue'),
        meta: {
          title: '투어닷 - 전국 관광 정보 & 맞춤형 여행지 추천 | 완벽한 여행의 시작',
          metaTags: [
            {
              name: 'description',
              content:
                '전국의 관광 명소, 축제, 맛집 정보를 지도로 탐색하세요. 공공데이터를 바탕으로 목적에 딱 맞는 맞춤형 여행지를 추천해 드립니다.',
            },
            { property: 'og:title', content: '투어닷 - 이번 주말 여행 장소, 여기서 결정하자!' },
            {
              property: 'og:description',
              content: '전국 방방곡곡의 관광 정보와 지역 축제를 지도로 간편하게 탐색하세요.',
            },
          ],
        },
      },
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
        alias: ['/kakao', '/naver', '/apple'],
        meta: {
          title: '로그인 | 투어닷',
          metaTags: [
            {
              name: 'description',
              content: '투어닷에 로그인하고 나만의 맞춤형 여행 코스와 숨겨진 전국 관광지 추천을 가장 먼저 받아보세요.',
            },
          ],
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        name: 'terms',
        path: 'terms',
        component: () => import('../pages/info/TermsView.vue'),
        meta: {
          title: '이용약관 | 투어닷',
          metaTags: [
            {
              name: 'description',
              content: '투어닷 서비스 이용약관입니다. 서비스 이용에 필요한 권리와 의무 사항을 확인하세요.',
            },
          ],
        },
      },
      {
        name: 'faq',
        path: 'faq',
        component: () => import('../pages/faq/FaqPage.vue'),
        meta: {
          title: '자주 묻는 질문(FAQ) | 투어닷',
          metaTags: [
            {
              name: 'description',
              content: '투어닷 서비스 이용에 대해 궁금한 점이 있으신가요? 자주 묻는 질문에서 확인해보세요.',
            },
          ],
        },
      },
      {
        name: 'privacy',
        path: 'privacy',
        component: () => import('../pages/info/PrivacyView.vue'),
        meta: {
          title: '개인정보처리방침 | 투어닷',
          metaTags: [
            {
              name: 'description',
              content: '투어닷은 사용자의 소중한 개인정보를 안전하게 보호합니다. 개인정보처리방침을 확인하세요.',
            },
          ],
        },
      },
      {
        name: 'location',
        path: 'location',
        component: () => import('../pages/info/LocationBasedView.vue'),
        meta: {
          title: '위치기반서비스 이용약관 | 투어닷',
          metaTags: [
            {
              name: 'description',
              content: '지도 기반 전국 관광지 및 여행 정보 탐색을 위한 위치기반서비스 이용약관 안내입니다.',
            },
          ],
        },
      },
      {
        name: 'refundUser',
        path: 'refundUser',
        component: () => import('../pages/info/RefundUserView.vue'),
        meta: {
          title: '사용자 환불 규정 | 투어닷',
          metaTags: [
            { name: 'description', content: '투어닷 서비스 이용 중 발생하는 환불에 대한 사용자 가이드라인입니다.' },
          ],
        },
      },
      {
        name: 'refundStore',
        path: 'refundStore',
        component: () => import('../pages/info/RefundStoreView.vue'),
        meta: {
          title: '스토어 환불 규정 | 투어닷',
          metaTags: [
            { name: 'description', content: '투어닷 입점 파트너를 위한 스토어 환불 및 정산 관련 규정 안내입니다.' },
          ],
        },
      },
      {
        name: 'jrs-verification',
        path: 'jrs-verification',
        component: () => import('../pages/admin/jrs/widgets/JrsVerification.vue'),
        meta: {
          hideHeader: true,
          hideFooter: true,
        },
      },
      {
        name: 'scraping',
        path: 'scraping',
        component: () => import('../pages/scraping/scraping.vue'),
      },
    ],
  },
  {
    name: 'admin',
    path: '/admin',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    meta: { requiresAuth: true },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/admin/users/UsersPage.vue'),
      },
      {
        name: 'notice',
        path: 'notice',
        component: () => import('../pages/admin/notice/NoticePage.vue'),
      },
      {
        name: 'orders',
        path: 'orders',
        component: () => import('../pages/admin/orders/OrdersPage.vue'),
      },
      {
        name: 'stores',
        path: 'stores',
        component: () => import('../pages/admin/stores/StoresPage.vue'),
      },
      {
        name: 'promotions',
        path: 'promotions',
        component: () => import('../pages/admin/promotions/PromotionsPage.vue'),
      },
      {
        name: 'jrs-manager',
        path: 'jrs-management',
        component: () => import('../pages/admin/jrs/JrsManagementPage.vue'),
      },
      {
        name: 'jrs-admin',
        path: 'jrs',
        children: [
          {
            name: 'jrs-card',
            path: 'jrs-card',
            component: () => import('../pages/admin/jrs/JrsManagementPage.vue'),
          },
          {
            name: 'jrs-list',
            path: 'jrs-list',
            component: () => import('../pages/admin/jrs/JrsAdminPage.vue'),
          },
          {
            name: 'jrs-assessment',
            path: 'jrs-assessment',
            component: () => import('../pages/admin/jrs/widgets/JrsAssessment.vue'),
          },
        ],
      },
      {
        name: 'ads',
        path: 'ads',
        component: RouteViewComponent,
        children: [
          {
            name: 'jrs-ads',
            path: 'jrs-ads',
            component: () => import('../pages/admin/ads/AdsPage.vue'),
          },
          {
            name: 'alarm-ads',
            path: 'alarm-ads',
            component: () => import('../pages/under-construction.vue'),
          },
          {
            name: 'search-suggestion-ads',
            path: 'search-suggestion-ads',
            component: () => import('../pages/under-construction.vue'),
          },
          {
            name: 'search-sns-ads',
            path: 'search-sns-ads',
            component: () => import('../pages/under-construction.vue'),
          },
        ],
      },
      {
        name: 'payments',
        path: 'payments',
        component: () => import('../pages/admin/payments/PaymentsPage.vue'),
      },
      // {
      //   name: 'payment-methods',
      //   path: 'payment-methods',
      //   component: () => import('../pages/payments/PaymentsPage.vue'),
      // },
      // {
      //   name: 'billing',
      //   path: 'billing',
      //   component: () => import('../pages/billing/BillingPage.vue'),
      // },
      // {
      //   name: 'pricing-plans',
      //   path: 'pricing-plans',
      //   component: () => import('../pages/pricing-plans/PricingPlans.vue'),
      // },
    ],
  },
  {
    path: '/app',
    component: RouteViewComponent,
    children: [
      {
        name: 'install',
        path: 'install',
        component: () => import('../pages/app/DeepLink.vue'),
      },
      {
        name: 'qrcode',
        path: 'qrcode',
        component: () => import('../pages/app/DeepLink.vue'),
      },
      {
        name: 'share',
        path: 'share',
        component: () => import('../pages/app/DeepLink.vue'),
      },
      {
        name: 'jrsForm',
        path: 'jrsForm',
        component: () => import('../pages/admin/jrs/widgets/JrsForm.vue'),
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to, from, next) => {
  // Allow public routes
  if (!to.matched.some((record) => record.meta.requiresAuth)) {
    return next()
  }

  const user = useUser()
  const isLogin = await user.isLoggedIn()

  if (isLogin) {
    return next() // ✅ prevent fall-through
  }

  // console test
  if (user.keepLoggedIn && user.refreshToken) {
    try {
      const tokenResponse = await refreshToken(user.$state)
      const userResponse = await getUser(user.userId)

      if (userResponse?.status === 200) {
        let userinfo: User = userResponse.data
        userinfo.accessToken = tokenResponse.accessToken
        userinfo.refreshToken = user.refreshToken
        userinfo.keepLoggedIn = user.keepLoggedIn
        userinfo.expiresIn = tokenResponse.expiresIn

        userinfo = setExpiryTimestamp(userinfo)

        user.setUser(userinfo)

        return next()
      }
    } catch (error: any) {
      let displayMessage = '로그인 정보를 불러오는 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.'

      if (error.response) {
        const status = error.response.status
        if (status === 401) {
          displayMessage = '로그인 세션이 만료되었습니다. 안전한 서비스 이용을 위해 다시 로그인해 주세요.'
        } else if (status >= 500) {
          displayMessage = '시스템 일시 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.'
        }
      } else if (error.message.includes('Network Error')) {
        displayMessage = '네트워크 연결이 원활하지 않습니다. 인터넷 상태를 확인한 후 다시 시도해 주세요.'
      }
      init({ message: displayMessage, color: 'success' })
    }
    return user.logout()
  }
  // Final fallback
  return next({ name: 'login', query: { redirect: to.fullPath } })
})

router.afterEach((to) => {
  const title = to.meta.title as string
  if (title) {
    document.title = title
  }

  const metaTags = to.meta.metaTags as Array<any>
  if (metaTags) {
    metaTags.forEach((tag) => {
      const selector = tag.name ? `meta[name="${tag.name}"]` : `meta[property="${tag.property}"]`
      let element = document.querySelector(selector)

      if (element) {
        element.setAttribute('content', tag.content)
      } else {
        element = document.createElement('meta')
        if (tag.name) element.setAttribute('name', tag.name)
        if (tag.property) element.setAttribute('property', tag.property)
        element.setAttribute('content', tag.content)
        document.head.appendChild(element)
      }
    })
  }
})

export default router
